import type { TApplicantStatus } from '@/services/applicants';
import type { TMemberEmailPurpose } from '@/services/emailSends';
import type { TInterviewEnding, TReleaseOutcome } from '@/services/interviews';

import type { TSegmentedOption } from 'components/ui/SegmentedToggle';

type TInterviewChannel = 'video_meet' | 'facetime';

type TInterviewChannelOption = TSegmentedOption & {
  value: TInterviewChannel;
};

type TInterviewOutcomeOption = TSegmentedOption & {
  value: TReleaseOutcome;
  description: string;
  isAttempt: boolean;
};

const INTERVIEW_RELEASE_ACTION = 'interview_release';

const INTERVIEW_COMPLETE_ACTION = 'interview_complete';

const COMPLETED_ENDING: TInterviewEnding = 'completed';

const RESCHEDULED_ENDING: TInterviewEnding = 'rescheduled';

const NO_SHOW_OUTCOME: TReleaseOutcome = 'no_show';

const RESCHEDULED_OUTCOME: TReleaseOutcome = 'rescheduled';

const INTERVIEW_RELEASE_PURPOSE: Record<TReleaseOutcome, TMemberEmailPurpose> = {
  no_show: 'interview_no_show',
  rescheduled: 'interview_rescheduled',
};

const INTERVIEW_CALL_STATUS: TApplicantStatus = 'interview_scheduled';

const FACETIME_CHANNEL: TInterviewChannel = 'facetime';

const VIDEO_MEET_CHANNEL: TInterviewChannel = 'video_meet';

const INTERVIEW_CHANNEL_OPTIONS: readonly TInterviewChannelOption[] = [
  { value: 'video_meet', label: 'Google Meet' },
  { value: 'facetime', label: 'FaceTime' },
];

const INTERVIEW_OUTCOME_OPTIONS: readonly TInterviewOutcomeOption[] = [
  {
    value: 'no_show',
    label: 'No-show',
    description: 'The applicant did not join. The booking is closed, and an email asks them to book a new slot.',
    isAttempt: true,
  },
  {
    value: 'rescheduled',
    label: 'Rescheduled',
    description: 'You agreed on another time. The Calendly event is canceled, and an email asks the applicant to book the new slot.',
    isAttempt: false,
  },
];

const INTERVIEW_CALL_COPY = {
  title: 'Interview call',
  allowedPrefix: 'Preferred way to connect:',
  callFaceTime: 'Call on FaceTime',
  joinMeet: 'Join Google Meet',
  meetPending: 'The Meet link has not arrived from Calendly yet.',
  release: 'No-show or reschedule',
  complete: 'Interview completed',
  completedPrefix: 'Completed',
  completeTitle: 'Interview completed',
  completeQuestion: 'Mark the call on',
  completeQuestionTail: 'as completed?',
  completeEffect: 'The booking closes and can no longer be marked as a no-show or rescheduled. The score, the rubric and the decision open.',
  completeAhead: 'The slot is still ahead: the Calendly event is canceled, and Calendly emails the applicant about the cancellation.',
  outcomeTitle: 'Interview outcome',
  outcomeQuestion: 'What happened to the call on',
  attemptsAfter: 'Attempts after this',
  attemptsHint: 'Nothing is blocked at the limit — the recruiter decides whether to continue.',
  attemptsOf: 'of',
  noShowLocked: 'A no-show can be marked once the call has started.',
  calendlyNotCanceled: 'Calendly event not canceled — cancel it in your Calendly',
  letterNotSent: 'Email not sent',
  confirm: 'Confirm',
  cancel: 'Cancel',
} as const;

const INTERVIEW_RELEASE_ERROR_COPY: Record<string, string> = {
  '42501': 'Your role cannot release interviews.',
  'P0002': 'This booking no longer exists.',
  'unknown_outcome': 'Choose what happened to the call.',
  'interview_not_live': 'This booking was already canceled or marked as a no-show.',
  'interview_not_started': 'A no-show can be marked once the call has started.',
  'applicant_not_scheduled': 'The applicant is no longer scheduled for this interview. Refresh the record.',
};

const INTERVIEW_COMPLETE_ERROR_COPY: Record<string, string> = {
  '42501': 'Your role cannot mark interviews as completed.',
  'P0002': 'This booking no longer exists.',
  'interview_not_live': 'This booking was already canceled or marked as a no-show.',
};

export type {
  TInterviewChannel,
  TInterviewChannelOption,
  TInterviewOutcomeOption,
};
export {
  COMPLETED_ENDING,
  FACETIME_CHANNEL,
  INTERVIEW_CALL_COPY,
  INTERVIEW_CALL_STATUS,
  INTERVIEW_CHANNEL_OPTIONS,
  INTERVIEW_COMPLETE_ACTION,
  INTERVIEW_COMPLETE_ERROR_COPY,
  INTERVIEW_OUTCOME_OPTIONS,
  INTERVIEW_RELEASE_ACTION,
  INTERVIEW_RELEASE_ERROR_COPY,
  INTERVIEW_RELEASE_PURPOSE,
  NO_SHOW_OUTCOME,
  RESCHEDULED_ENDING,
  RESCHEDULED_OUTCOME,
  VIDEO_MEET_CHANNEL,
};
